import React, { useState } from "react";
import {
  FaFileAlt,
  FaBookOpen,
  FaFileDownload,
  FaLightbulb,
  FaLock,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const allResources = [
  // Whitepapers only for simplicity
  // {
  //   title: "RoomRounds Case Study 75 Bed SNF",
  //   type: "Whitepaper",
  //   link: "#",
  //   icon: <FaFileAlt className="text-[#1E40AF] text-2xl mb-2" />,
  //   tab: "Case Studies",
  //   gated: true,
  // },
  {
    title: "RoomRounds Whitepaper Operational Data Layer",
    type: "Whitepaper",
    link: "#",
    icon: <FaFileAlt className="text-[#1E40AF] text-2xl mb-2" />,
    tab: "Case Studies",
    gated: true,
  },
];

const tabs = ["Case Studies"];

const fadeIn = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, type: "spring" as const },
  },
};

const Resources: React.FC = () => {
  const [activeTab, setActiveTab] = useState("Case Studies");
  const [showModal, setShowModal] = useState(false);
  const [modalResource, setModalResource] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    facility: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const filtered = allResources.filter((r) => r.tab === activeTab);

  const handleWhitepaperClick = (resource: any) => {
    setModalResource(resource);
    setShowModal(true);
  };

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Here you can send formData to your marketing/CRM API
    console.log("Lead captured:", formData);

    setSubmitted(true);

    // After short delay, allow download
    setTimeout(() => {
      handleDummyDownload(modalResource);
      setShowModal(false);
      setSubmitted(false);
      setFormData({ name: "", email: "", phone: "", facility: "" });
    }, 500);
  };

  const resourceDownloads: Record<string, { href: string; filename: string }> =
    {
      "RoomRounds Case Study 75Bed SNF": {
        href: "/RoomRounds_Case_Study_75Bed_SNF.pdf",
        filename: "RoomRounds Case Study 75Bed SNF.pdf",
      },
      "RoomRounds Whitepaper Operational Data Layer": {
        href: "/RoomRounds_White_Paper_Operational_Data_Layer.pdf",
        filename: "RoomRounds Whitepaper Operational Data Layer.pdf",
      },
    };

  const handleDummyDownload = (resource: { title: string }) => {
    const download = resourceDownloads[resource.title];
    if (!download) return;

    const link = document.createElement("a");
    link.href = download.href;
    link.download = download.filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
  };

  return (
    <section className="max-w-5xl mx-auto py-12 px-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="mb-12 text-center"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-blue-800 drop-shadow">
          Resources & Insights
        </h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto">
          Explore our latest downloadable whitepapers. Fill out the form to
          access them instantly.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
        <AnimatePresence>
          {filtered.map((res) => (
            <motion.div
              key={res.title}
              className="bg-white rounded-2xl shadow-lg p-6 flex flex-col items-center text-center hover:shadow-2xl transition min-h-[220px]"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeIn}
            >
              {res.icon}
              <h3 className="text-lg font-bold mb-2 text-blue-800">
                {res.title}
              </h3>
              <div className="text-gray-500 text-xs mb-2">{res.type}</div>
              {res.gated ? (
                <button
                  className="bg-[#1E40AF] text-white px-4 py-2 rounded-lg shadow hover:bg-green-700 transition flex items-center gap-2 font-semibold"
                  onClick={() => handleWhitepaperClick(res)}
                >
                  <FaFileDownload /> Download
                </button>
              ) : (
                <Link
                  to={res.link}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-700 transition font-semibold"
                >
                  Read More
                </Link>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Lead Capture Modal */}
      <AnimatePresence>
        {showModal && modalResource && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-lg max-w-md w-full mx-4 p-8 relative flex flex-col items-center"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-2 text-blue-800">
                Download: {modalResource.title}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Please fill out the form to access this whitepaper.
              </p>
              {submitted ? (
                <div className="text-[#1E40AF] font-semibold text-center">
                  Thank you! Your whitepaper is downloading...
                </div>
              ) : (
                <form
                  onSubmit={handleLeadSubmit}
                  className="w-full flex flex-col items-center gap-4"
                >
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Facility Name"
                    value={formData.facility}
                    onChange={(e) =>
                      setFormData({ ...formData, facility: e.target.value })
                    }
                    required
                    className="w-full border rounded px-3 py-2"
                  />
                  <button
                    type="submit"
                    className="bg-[#1E40AF] text-white px-6 py-2 rounded-lg shadow hover:bg-green-700 transition font-semibold w-full"
                  >
                    Get Whitepaper
                  </button>
                </form>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
                title="Close"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Resources;
